import { createQueryHook } from '~/shared/providers/api';

export interface FacilityRoleRecord {
  id: string;
  name: string;
  userCount: number;
  permissions: string[];
  updatedAt: string | null;
  updatedBy: string | null;
}

export interface FacilityRoleUserRecord {
  id: string;
  name: string;
  email: string;
  phoneNumber: string | null;
  assignmentId: string;
}

export interface FacilityRolesQueryParams {
  facilityId: string;
  limit?: number;
  offset?: number;
}

export interface FacilityRolesQueryResult {
  roles: FacilityRoleRecord[];
  users: Map<string, FacilityRoleUserRecord[]>;
}

export const useFacilityRolesQuery = createQueryHook(
  'settings.teamPermissions.facilityRoles',
  (params: FacilityRolesQueryParams) => ({
    coreUserRoleAssignments: {
      __args: {
        filter: {
          facilityIds: [params.facilityId],
          pagination: {
            limit: params.limit ?? 1000,
            offset: params.offset ?? 0,
          },
        },
      },
      entries: {
        id: true,
        role: {
          id: true,
          name: true,
          permissions: true,
          timestamps: {
            updatedAt: true,
          },
        },
        user: {
          id: true,
          firstName: true,
          lastName: true,
          email: true,
          phoneNumber: true,
        },
      },
      page: {
        total: true,
      },
    },
  }),
  {
    select: (data): FacilityRolesQueryResult => {
      const entries = data?.coreUserRoleAssignments?.entries ?? [];

      const roleMap = new Map<string, FacilityRoleRecord>();
      const usersMap = new Map<string, FacilityRoleUserRecord[]>();

      entries.forEach((assignment) => {
        const role = assignment.role;
        const user = assignment.user;
        if (!role) return;

        if (!roleMap.has(role.id)) {
          roleMap.set(role.id, {
            id: role.id,
            name: role.name,
            userCount: 0,
            permissions: role.permissions ?? [],
            updatedAt: role.timestamps?.updatedAt ?? null,
            updatedBy: null,
          });
          usersMap.set(role.id, []);
        }

        if (user) {
          const roleRecord = roleMap.get(role.id);
          const roleUsers = usersMap.get(role.id);
          if (!roleRecord || !roleUsers) return;
          const existingUser = roleUsers.find((u) => u.id === user.id);

          if (!existingUser) {
            roleRecord.userCount += 1;
            roleUsers.push({
              id: user.id,
              name:
                `${user.firstName ?? ''} ${user.lastName ?? ''}`.trim() || '-',
              email: user.email ?? '',
              phoneNumber: user.phoneNumber ?? null,
              assignmentId: assignment.id,
            });
          }
        }
      });

      return {
        roles: Array.from(roleMap.values()),
        users: usersMap,
      };
    },
  },
);
